import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Brain, RefreshCw, Users, Bot, Building2, Server,
  AlertTriangle, CheckCircle2, XCircle, ExternalLink, TrendingUp,
  ShieldOff, Sparkles, Cable,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'

interface AppRow { name: string; external_id?: string; distribution?: string; likely_agent?: boolean }
interface SkuRow { sku: string; used: number; total: number }
interface UserRow {
  user: string
  display_name: string
  last_activity: string
  apps: string[] | Record<string, string>
  app_count: number
}

interface CopilotData {
  enabled: boolean
  error?: string
  note?: string
  period?: string
  licensed_users?: number
  active_users?: number
  adoption_pct?: number | null
  by_app?: Record<string, number>
  sku_breakdown?: SkuRow[]
  sku_used?: number
  sku_total?: number
  top_users?: UserRow[]
  inactive_sample?: UserRow[]
}

interface StudioData {
  enabled: boolean
  error?: string
  total_org_apps?: number
  agent_count?: number
  agents?: AppRow[]
  all_apps_sample?: AppRow[]
}

interface FoundryResource {
  name: string
  id: string
  location: string
  resource_group: string
  subscription_id: string
  kind?: string
}

interface DeploymentRow {
  name: string
  account: string
  account_rg: string
  model: string
  model_version: string
  sku?: string
  capacity?: number
  provisioning_state?: string
  rate_limits?: any[]
}

interface FoundryData {
  enabled: boolean
  error?: string
  subscription_count?: number
  hubs?: FoundryResource[]
  projects?: FoundryResource[]
  ml_workspaces?: FoundryResource[]
  openai_accounts?: FoundryResource[]
  cognitive_accounts?: FoundryResource[]
  deployments?: DeploymentRow[]
  model_totals?: Record<string, number>
}

interface ConsoleData {
  copilot: CopilotData
  studio: StudioData
  foundry: FoundryData
}

type Tab = 'overview' | 'copilot' | 'studio' | 'foundry' | 'deployments'

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview',    label: 'Overview',         icon: Brain },
  { id: 'copilot',     label: 'Copilot (M365)',   icon: Sparkles },
  { id: 'studio',      label: 'Copilot Studio',   icon: Bot },
  { id: 'foundry',     label: 'AI Foundry',       icon: Building2 },
  { id: 'deployments', label: 'Model Deployments', icon: Server },
]

function Stat({ label, value, sub, tone = 'text' }: { label: string; value: string | number; sub?: string; tone?: string }) {
  const color = {
    emerald: 'text-emerald',
    rose: 'text-rose',
    amber: 'text-amber-light',
    blue: 'text-blue-light',
    text: 'text-text-primary',
  }[tone] || 'text-text-primary'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">{label}</div>
      <div className={clsx('text-xl font-bold font-mono mt-0.5', color)}>{value}</div>
      {sub && <div className="text-[10px] text-text-muted mt-0.5">{sub}</div>}
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="card p-3 border border-rose/30 bg-rose/5 flex items-start gap-2">
      <AlertTriangle size={14} className="text-rose shrink-0 mt-0.5" />
      <div className="text-[11px] text-rose-light">{message}</div>
    </div>
  )
}

function DisabledBanner({ message }: { message: string }) {
  return (
    <div className="card p-3 border border-amber/30 bg-amber/5 flex items-start gap-3">
      <ShieldOff size={16} className="text-amber shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-[12px] font-semibold text-amber-light">{message}</div>
        <div className="text-[10px] text-text-secondary mt-1">
          Configure the Entra SSO app in <Link to="/app/admin" className="text-blue-light hover:underline">Admin → Source Code</Link>{' '}
          and grant the extra permissions (Reports.Read.All, Azure RBAC Reader) per the{' '}
          <Link to="/app/wiki" className="text-blue-light hover:underline">knowledge base</Link>.
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AgentsConsolePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'overview')
  const [period, setPeriod] = useState<string>(searchParams.get('period') || 'D7')
  const [data, setData] = useState<ConsoleData | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/agents/console', { params: { period } })
      setData(data)
    } finally { setLoading(false) }
  }, [period])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const next: Record<string, string> = { tab }
    if (period !== 'D7') next.period = period
    setSearchParams(next, { replace: true })
  }, [tab, period, setSearchParams])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  if (!data) return null

  const { copilot, studio, foundry } = data

  // ── Overview ──────────────────────────────────────────────────────────────
  const overviewTab = (
    <div className="space-y-3">
      {copilot.note && <DisabledBanner message={copilot.note} />}
      {copilot.error && <ErrorBanner message={`Copilot: ${copilot.error}`} />}
      {studio.error && <ErrorBanner message={`Copilot Studio: ${studio.error}`} />}
      {foundry.error && <ErrorBanner message={`AI Foundry: ${foundry.error}`} />}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="Copilot licenced"
              value={copilot.licensed_users ?? copilot.sku_used ?? '—'}
              sub={copilot.sku_total ? `of ${copilot.sku_total} SKU seats` : undefined}
              tone="blue" />
        <Stat label={`Active ${period}`}
              value={copilot.active_users ?? '—'}
              sub={copilot.adoption_pct != null ? `${copilot.adoption_pct}% adoption` : undefined}
              tone={copilot.adoption_pct != null && copilot.adoption_pct < 30 ? 'amber' : 'emerald'} />
        <Stat label="Studio agents"
              value={studio.agent_count ?? '—'}
              sub={studio.total_org_apps ? `of ${studio.total_org_apps} org apps` : undefined}
              tone="blue" />
        <Stat label="Foundry hubs"
              value={(foundry.hubs?.length ?? 0) + (foundry.projects?.length ?? 0)}
              sub={foundry.projects?.length ? `${foundry.projects.length} projects` : undefined}
              tone="blue" />
        <Stat label="Azure OpenAI accts"
              value={foundry.openai_accounts?.length ?? '—'}
              sub={foundry.cognitive_accounts?.length ? `${foundry.cognitive_accounts.length} cog total` : undefined}
              tone="blue" />
        <Stat label="Model deployments"
              value={foundry.deployments?.length ?? '—'}
              sub={Object.keys(foundry.model_totals ?? {}).length + ' distinct models'}
              tone="blue" />
      </div>

      {copilot.by_app && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-text-primary">Copilot activity by app ({copilot.period ?? period})</h2>
            <span className="text-[10px] text-text-muted">{copilot.active_users} of {copilot.licensed_users} licensed</span>
          </div>
          <div className="space-y-1">
            {Object.entries(copilot.by_app).sort(([, a], [, b]) => b - a).map(([app, n]) => {
              const pct = copilot.licensed_users ? Math.round(n / copilot.licensed_users * 100) : 0
              return (
                <div key={app} className="flex items-center gap-2">
                  <span className="w-24 text-[11px] text-text-secondary truncate">{app}</span>
                  <div className="flex-1 bg-surface-raised rounded-full h-3 overflow-hidden relative">
                    <div
                      className={clsx('h-full',
                        pct >= 50 ? 'bg-emerald' : pct >= 25 ? 'bg-amber' : 'bg-rose/60'
                      )}
                      style={{ width: `${pct}%` }}
                    />
                    <span className="absolute inset-0 flex items-center justify-end pr-2 text-[9px] font-mono text-text-primary">
                      {n} ({pct}%)
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {foundry.model_totals && Object.keys(foundry.model_totals).length > 0 && (
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-2">Top models deployed</h2>
            <table className="w-full text-[11px]">
              <tbody>
                {Object.entries(foundry.model_totals).sort(([, a], [, b]) => b - a).slice(0, 10).map(([m, n]) => (
                  <tr key={m} className="border-b border-border/50 last:border-0">
                    <td className="py-1.5 text-text-primary">{m}</td>
                    <td className="py-1.5 text-right font-mono text-blue-light">{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {copilot.sku_breakdown && copilot.sku_breakdown.length > 0 && (
          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-2">Copilot SKU utilisation</h2>
            <div className="space-y-1.5">
              {copilot.sku_breakdown.map(s => {
                const pct = s.total ? Math.round(s.used / s.total * 100) : 0
                return (
                  <div key={s.sku}>
                    <div className="flex justify-between text-[11px]">
                      <span className="font-mono text-text-primary">{s.sku}</span>
                      <span className="text-text-muted">{s.used} / {s.total}</span>
                    </div>
                    <div className="bg-surface-raised rounded-full h-1.5 overflow-hidden">
                      <div className={clsx('h-full', pct >= 90 ? 'bg-rose' : pct >= 70 ? 'bg-amber' : 'bg-emerald')}
                           style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  // ── Copilot tab (detail) ──────────────────────────────────────────────────
  const copilotTab = (
    <div className="space-y-3">
      {copilot.error && <ErrorBanner message={copilot.error} />}
      {copilot.note && <DisabledBanner message={copilot.note} />}

      {copilot.top_users && copilot.top_users.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <TrendingUp size={14} className="text-emerald" /> Top 15 most engaged users
            </h2>
          </div>
          <table className="w-full text-left">
            <thead className="bg-surface-raised/40">
              <tr className="border-b border-border text-[10px] text-text-muted">
                <th className="py-2 px-3 font-semibold">User</th>
                <th className="py-2 px-3 font-semibold">Apps active</th>
                <th className="py-2 px-3 font-semibold">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {copilot.top_users.map(u => (
                <tr key={u.user} className="border-b border-border/50">
                  <td className="py-1.5 px-3 text-[11px]">
                    <div className="font-semibold text-text-primary">{u.display_name || u.user}</div>
                    <div className="text-[9px] text-text-muted">{u.user}</div>
                  </td>
                  <td className="py-1.5 px-3">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(u.apps) ? u.apps : Object.keys(u.apps)).map(a => (
                        <span key={a} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald/10 text-emerald border border-emerald/30">
                          {a}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-1.5 px-3 text-[10px] text-text-muted font-mono">{u.last_activity?.slice(0, 10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {copilot.inactive_sample && copilot.inactive_sample.length > 0 && (
        <div className="card overflow-hidden border border-rose/20">
          <div className="p-3 border-b border-border flex items-center justify-between bg-rose/5">
            <h2 className="text-sm font-semibold text-rose-light flex items-center gap-2">
              <AlertTriangle size={14} /> Licensed but inactive ({copilot.inactive_sample.length} shown)
            </h2>
          </div>
          <table className="w-full text-left">
            <tbody>
              {copilot.inactive_sample.map(u => (
                <tr key={u.user} className="border-b border-border/50">
                  <td className="py-1.5 px-3 text-[11px]">
                    <div className="font-semibold text-text-primary">{u.display_name || u.user}</div>
                    <div className="text-[9px] text-text-muted">{u.user}</div>
                  </td>
                  <td className="py-1.5 px-3 text-[9px] text-rose">no activity in {copilot.period ?? period}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-2 text-[10px] text-text-muted border-t border-border">
            These users hold a Copilot licence but haven't touched it. Candidates for reassignment.
          </div>
        </div>
      )}
    </div>
  )

  // ── Copilot Studio tab ────────────────────────────────────────────────────
  const studioTab = (
    <div className="space-y-3">
      {studio.error && <ErrorBanner message={studio.error} />}
      {!studio.enabled && <DisabledBanner message="Entra SSO creds not configured" />}

      {studio.agents && studio.agents.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-3 border-b border-border">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Bot size={14} className="text-blue" /> Custom Copilot / agent apps ({studio.agents.length})
            </h2>
            <div className="text-[10px] text-text-muted mt-0.5">
              Teams apps distributed internally whose name suggests a Copilot / agent / bot. Detection is heuristic — exact
              Copilot Studio inventory lives in Power Platform admin centre.
            </div>
          </div>
          <table className="w-full">
            <tbody>
              {studio.agents.map(a => (
                <tr key={a.external_id || a.name} className="border-b border-border/50">
                  <td className="py-1.5 px-3 text-[11px]">
                    <div className="font-semibold text-text-primary flex items-center gap-1.5">
                      <Bot size={11} className="text-blue" /> {a.name}
                    </div>
                    {a.external_id && <div className="text-[9px] text-text-muted font-mono">{a.external_id}</div>}
                  </td>
                  <td className="py-1.5 px-3 text-[10px] text-text-muted">{a.distribution}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {studio.all_apps_sample && studio.all_apps_sample.length > 0 && (
        <details className="card p-3">
          <summary className="text-[11px] text-text-secondary cursor-pointer">
            All {studio.total_org_apps} organisation-distributed Teams apps (sample of 25)
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {studio.all_apps_sample.map(a => (
              <span key={a.external_id || a.name} className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded border',
                a.likely_agent
                  ? 'bg-blue/10 border-blue/30 text-blue-light'
                  : 'bg-surface-raised border-border text-text-secondary',
              )}>
                {a.name}
              </span>
            ))}
          </div>
        </details>
      )}

      <div className="card p-3 text-[10px] text-text-muted">
        For the authoritative Copilot Studio agent inventory (including traffic, conversations, handoffs), open{' '}
        <a href="https://copilotstudio.microsoft.com" target="_blank" rel="noreferrer noopener" className="text-blue-light hover:underline inline-flex items-center gap-0.5">
          copilotstudio.microsoft.com <ExternalLink size={9} />
        </a>
        {' '}— the Power Platform admin API would let this page show them directly if the connector is extended.
      </div>
    </div>
  )

  // ── Foundry tab ───────────────────────────────────────────────────────────
  const foundryTab = (
    <div className="space-y-3">
      {foundry.error && <ErrorBanner message={foundry.error} />}

      {foundry.hubs && foundry.hubs.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
            <Building2 size={14} className="text-blue" /> Foundry hubs ({foundry.hubs.length})
          </h2>
          <div className="card overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-surface-raised/40">
                <tr className="border-b border-border text-[10px] text-text-muted">
                  <th className="py-2 px-3 text-left">Name</th>
                  <th className="py-2 px-3 text-left">Region</th>
                  <th className="py-2 px-3 text-left">Resource group</th>
                </tr>
              </thead>
              <tbody>
                {foundry.hubs.map(h => (
                  <tr key={h.id} className="border-b border-border/50">
                    <td className="py-1.5 px-3 text-text-primary font-semibold">{h.name}</td>
                    <td className="py-1.5 px-3 text-text-muted">{h.location}</td>
                    <td className="py-1.5 px-3 text-text-muted font-mono">{h.resource_group}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {foundry.projects && foundry.projects.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-primary mb-2">Foundry projects ({foundry.projects.length})</h2>
          <div className="card overflow-hidden">
            <table className="w-full text-[11px]">
              <tbody>
                {foundry.projects.map(p => (
                  <tr key={p.id} className="border-b border-border/50">
                    <td className="py-1.5 px-3 text-text-primary font-semibold">{p.name}</td>
                    <td className="py-1.5 px-3 text-text-muted">{p.location}</td>
                    <td className="py-1.5 px-3 text-text-muted font-mono">{p.resource_group}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {foundry.openai_accounts && foundry.openai_accounts.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-primary mb-2">Azure OpenAI accounts ({foundry.openai_accounts.length})</h2>
          <div className="card overflow-hidden">
            <table className="w-full text-[11px]">
              <tbody>
                {foundry.openai_accounts.map(a => (
                  <tr key={a.id} className="border-b border-border/50">
                    <td className="py-1.5 px-3 text-text-primary font-semibold">{a.name}</td>
                    <td className="py-1.5 px-3 text-text-muted">{a.location}</td>
                    <td className="py-1.5 px-3 text-text-muted font-mono">{a.resource_group}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )

  // ── Deployments tab ────────────────────────────────────────────────────────
  const deploymentsTab = (
    <div className="space-y-3">
      {foundry.error && <ErrorBanner message={foundry.error} />}
      {foundry.deployments && foundry.deployments.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-surface-raised/40">
              <tr className="border-b border-border text-[10px] text-text-muted">
                <th className="py-2 px-3 text-left">Deployment</th>
                <th className="py-2 px-3 text-left">Model</th>
                <th className="py-2 px-3 text-left">SKU</th>
                <th className="py-2 px-3 text-right">Capacity</th>
                <th className="py-2 px-3 text-left">Account</th>
                <th className="py-2 px-3 text-left">State</th>
                <th className="py-2 px-3 text-right">TPM / RPM</th>
              </tr>
            </thead>
            <tbody>
              {foundry.deployments.map((d, i) => {
                const tpm = (d.rate_limits || []).find((r: any) => r.key === 'request' || r.key === 'token')
                return (
                  <tr key={`${d.account}-${d.name}-${i}`} className="border-b border-border/50">
                    <td className="py-1.5 px-3 text-text-primary font-semibold">{d.name}</td>
                    <td className="py-1.5 px-3">
                      <span className="font-mono text-blue-light">{d.model}</span>
                      {d.model_version && <span className="text-text-muted ml-1">{d.model_version}</span>}
                    </td>
                    <td className="py-1.5 px-3 text-text-muted">{d.sku || '—'}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-text-secondary">{d.capacity ?? '—'}</td>
                    <td className="py-1.5 px-3 text-text-muted">{d.account}</td>
                    <td className="py-1.5 px-3">
                      <span className={clsx('text-[10px] font-semibold',
                        d.provisioning_state === 'Succeeded' ? 'text-emerald' : 'text-amber-light'
                      )}>
                        {d.provisioning_state || '—'}
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-right text-[10px] font-mono text-text-muted">
                      {(d.rate_limits || []).map((r: any) => `${r.count}${r.renewalPeriod === 60 ? '/min' : ''}`).join(' · ') || '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-6 text-center text-[11px] text-text-muted">
          No model deployments found — this shows Azure OpenAI + Cognitive Services deployments
          across the first 15 accounts visible to the app.
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Bot size={20} className="text-blue" /> Agent Console
          </h1>
          <p className="text-[10px] text-text-secondary">
            Unified view of Microsoft 365 Copilot adoption · Copilot Studio agents · Azure AI Foundry hubs/projects · Azure OpenAI deployments.
            Reuses the Entra SSO app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="input py-1 text-[11px] pr-6"
            title="Usage reporting window"
          >
            <option value="D7">Last 7 days</option>
            <option value="D30">Last 30 days</option>
            <option value="D90">Last 90 days</option>
            <option value="D180">Last 180 days</option>
          </select>
          <button type="button" onClick={fetchData} className="btn-ghost text-xs" title="Refresh" aria-label="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <Link to="/app/connectors-manage" className="btn-secondary text-xs inline-flex items-center gap-1">
            <Cable size={11} /> Connectors
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap',
              tab === t.id
                ? 'border-blue text-blue-light'
                : 'border-transparent text-text-muted hover:text-text-primary',
            )}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {tab === 'overview'    && overviewTab}
      {tab === 'copilot'     && copilotTab}
      {tab === 'studio'      && studioTab}
      {tab === 'foundry'     && foundryTab}
      {tab === 'deployments' && deploymentsTab}
    </div>
  )
}
